import React, { memo, useMemo, useCallback } from 'react';
import { TableRow, TableCell } from '@material-ui/core';
import { CSS } from '@dnd-kit/utilities';
import { useStyles } from '../DiagnosticRevisionTable.style';
import { formatNestedFinding, getProjectionString, processPriors } from '../helpers/dataProcessing';
import { isValidProjectionMatch } from '../utils/utils';
import { useDragDrop } from '../hooks/useDragDrop';
import IndexCell from './IndexCell';
import TypeChip from './TypeChip';
import ClassCell from './ClassCell';
import SizeCell from './SizeCell';
import HistoryCell from './HistoryCell';
import { Quadrant } from './Quadrant';
import RowActions from './RowActions';
import { useSelector } from 'react-redux';

const computeRowIsExpanded = (lesionAllInfo, expandedClusters) => {
  if (!lesionAllInfo || !expandedClusters) return false;
  const isMatchedMainRow = !!lesionAllInfo.isMatchedMainRow;
  const isClustered = !!(lesionAllInfo.clustered && !isMatchedMainRow);
  if (!isClustered && !isMatchedMainRow) return false;
  return !!expandedClusters[lesionAllInfo?.object_id || null];
};

const matchesJustEditedHighlight = (id, justEdited) => {
  if (id == null || id === '' || justEdited == null || justEdited === '') return false;
  if (id === justEdited) return true;
  const idStr = String(id);
  const editedStr = String(justEdited);
  return idStr.includes(editedStr) || editedStr.includes(idStr);
};

const getGroupedObjectId = (lesionAllInfo, isClustered, isMatchedMainRow) => {
  if (!lesionAllInfo) return null;
  if (isClustered || isMatchedMainRow) {
    return lesionAllInfo?.object_id || null;
  }
  return null;
};

const DiagnosticsTableRow = ({
  lesionAllInfo,
  index,
  nested = false,
  matchNestedTable = false,
  expandedClusters,
  setExpandedClusters,
  draggingLesionId,
  justEditedRows,
  exam_id,
  allFindings,
  onClassChange,
  onTypeChange,
  onUnmatch,
  onRowEnter,
  onRowLeave,
  onDelete,
  pinnedRow,
  setPinnedRow,
  smallScreen,
}) => {
  const classes = useStyles();
  const singleBoxHover = useSelector((state) => state.labeling?.singleBoxHover);

  if (!lesionAllInfo) return null;

  const draggingActive = !!draggingLesionId;
  const isMatchedMainRow = lesionAllInfo.isMatchedMainRow;
  const isClustered = lesionAllInfo.clustered && !isMatchedMainRow;
  const matched = lesionAllInfo?.matched_lesions;
  const module_type = lesionAllInfo?.module_type;
  const projection = Array.isArray(lesionAllInfo.projection)
    ? lesionAllInfo.projection[0]
    : lesionAllInfo.projection;

  const finding_class = lesionAllInfo.finding_class;

  const ccFinding = lesionAllInfo.matched_lesions?.find((f) => {
    const proj = Array.isArray(f.projection) ? f.projection[0] : f.projection;
    return proj && proj.includes('cc');
  });
  const mloFinding = lesionAllInfo.matched_lesions?.find((f) => {
    const proj = Array.isArray(f.projection) ? f.projection[0] : f.projection;
    return proj && proj.includes('mlo');
  });
  const matchedLesionAllInfo = mloFinding || ccFinding;
  const groupedLesionId = lesionAllInfo?.object_id || null;
  const rowPinId = lesionAllInfo?.object_id || lesionAllInfo?.finding_id;
  const isPinnedRow = !!pinnedRow && pinnedRow === rowPinId;

  const lesionPriors = useMemo(() => {
    if (isMatchedMainRow && Array.isArray(lesionAllInfo?.priors)) {
      return lesionAllInfo.priors;
    }
    return processPriors(lesionAllInfo, nested && matched ? matchedLesionAllInfo : null);
  }, [lesionAllInfo?.priors, lesionAllInfo?.isMatchedMainRow, lesionAllInfo?.matched_lesions]);

  // Use pre-computed allFindings from parent instead of calling mergeFindings per row
  const draggedBox = draggingLesionId ? allFindings?.find((b) => b.finding_id === draggingLesionId) : null;
  const draggedProjection = getProjectionString(draggedBox?.projection);
  const currentProjection = getProjectionString(projection);
  const projectionMatch =
    draggedProjection && currentProjection
      ? isValidProjectionMatch(draggedProjection, currentProjection)
      : false;
  const isSameLesion = lesionAllInfo?.finding_id === draggingLesionId;
  const isPossibleMatch =
    module_type === 'opacities'
      ? draggedBox && projectionMatch && !matched && !isClustered && !isMatchedMainRow && !isSameLesion
      : false;

  const isExpanded =
    expandedClusters && expandedClusters[groupedLesionId] && (isClustered || isMatchedMainRow);

  const isHovered = useMemo(
    () =>
      !!(
        singleBoxHover &&
        (((isClustered || isMatchedMainRow) && singleBoxHover?.lesionID === groupedLesionId) ||
          (singleBoxHover?.findingID && singleBoxHover?.findingID === lesionAllInfo?.finding_id))
      ),
    [singleBoxHover, isClustered, isMatchedMainRow, groupedLesionId, lesionAllInfo]
  );

  const isMainHovered = useMemo(() => {
    return isExpanded && isMatchedMainRow && singleBoxHover?.lesionID === lesionAllInfo?.object_id;
  }, [isExpanded, isMatchedMainRow, singleBoxHover, lesionAllInfo]);

  const matchedNestedItems = useMemo(() => {
    if (!lesionAllInfo?.isMatchedMainRow || !lesionAllInfo?.matched_lesions) {
      return null;
    }
    return { matched_lesions: lesionAllInfo.matched_lesions };
  }, [lesionAllInfo]);

  const {
    dragAttributes,
    dragListeners,
    setNodeRef,
    isOver,
    draggedId,
    isDraggableDisabled,
    dragTransform,
    dragTransition,
  } = useDragDrop({
    uniqueFindingId: lesionAllInfo?.finding_id,
    lesionAllInfo,
    matched,
    isClustered,
    isMatchedMainRow,
    isPossibleMatch,
    draggedId: draggingLesionId,
    module_type,
  });

  const dragProps = useMemo(
    () => (!isClustered && module_type === 'opacities' ? { ...dragAttributes, ...dragListeners } : {}),
    [isClustered, module_type, dragAttributes, dragListeners]
  );

  // Style calculations
  const getRowStyle = (isHovered, isMainHovered) => {
    const getBackgroundColor = () => {
      if (isMainHovered) return '#3d4b4b';
      if (isHovered) return '#245353';
      if (isPossibleMatch) return '#3a5b48';
      if (isExpanded) return '#3b3b3b';
      if (nested) return '#3b3b3b';
      return undefined;
    };

    return {
      position: 'relative',
      transform: CSS.Translate.toString(dragTransform),
      transition: dragTransition,
      backgroundColor: getBackgroundColor(),
      opacity: draggingActive
        ? draggedId === lesionAllInfo?.finding_id
          ? 1
          : isPossibleMatch
          ? 0.9
          : 0.4
        : 1,
      outline: isOver ? '2px dashed rgb(255, 255, 255)' : '2px solid transparent',
      outlineOffset: '-2px',
      height: isExpanded && !nested ? '50px' : '70px',
      boxShadow:
        justEditedRows &&
        (matchesJustEditedHighlight(lesionAllInfo?.finding_id, justEditedRows) ||
          (groupedLesionId && matchesJustEditedHighlight(groupedLesionId, justEditedRows)))
          ? `rgb(80 96 99) 0px 0px 4px 2px`
          : isClustered && isExpanded
          ? 'rgb(47, 47, 47) 0px 2px 5px 0px inset'
          : undefined,
    };
  };

  const style = getRowStyle(isHovered, isMainHovered);

  const handleRowEnter = useCallback(() => {
    onRowEnter({ module_type, finding_class, projection, lesionAllInfo, isClustered, nested });
  }, [onRowEnter, module_type, finding_class, projection, lesionAllInfo, isClustered, nested]);

  const handleRowLeave = useCallback(() => {
    onRowLeave({ module_type, finding_class, projection, lesionAllInfo, isClustered, nested });
  }, [onRowLeave, module_type, finding_class, projection, lesionAllInfo, isClustered, nested]);

  const sharedRowProps = {
    expandedClusters,
    setExpandedClusters,
    draggingLesionId,
    justEditedRows,
    exam_id,
    allFindings,
    onClassChange,
    onTypeChange,
    onUnmatch,
    onRowEnter,
    onRowLeave,
    onDelete,
    pinnedRow,
    setPinnedRow,
    smallScreen,
  };

  return (
    <>
      <TableRow
        className={`${classes.table_row}`}
        ref={setNodeRef}
        style={style}
        {...dragProps}
        onMouseEnter={() => {
          if (!draggingActive) handleRowEnter();
        }}
        onMouseLeave={() => {
          if (!draggingActive) handleRowLeave();
        }}>
        {/* Index */}
        <TableCell
          className={`${classes.table_cell_general} ${classes.index_cell} `}
          align='center'
          style={{
            borderLeft: nested ? '2px solid #4c4c4c' : 'none',
            width: '7%',
            cursor:
              !isMatchedMainRow && !isClustered && isHovered && !nested && module_type !== 'microcalc'
                ? draggingActive
                  ? 'grabbing'
                  : 'grab'
                : 'default',
            padding: '0',
          }}>
          <IndexCell
            lesionAllInfo={lesionAllInfo}
            isClustered={isClustered}
            isMatchedMainRow={isMatchedMainRow}
            isExpanded={isExpanded}
            setExpandedClusters={setExpandedClusters}
            nested={nested}
            isHovered={isHovered}
            lesionId={groupedLesionId}
            findingId={lesionAllInfo?.finding_id}
            matched={matched}
            isPossibleMatch={isPossibleMatch}
            isDraggableDisabled={isDraggableDisabled}
            justEditedRows={justEditedRows}
            onClassChange={onClassChange}
            onUnmatch={onUnmatch}
          />
        </TableCell>

        {/* Type */}
        <TableCell
          style={{ width: '16%' }}
          align='center'
          className={classes.table_cell_general}
          data-no-drag='true'
          id='type_chip'
          onPointerDown={(e) => {
            if (e) {
              e.stopPropagation();
              e.preventDefault();
            }
          }}>
          <div
            className={classes.general_cell_container}
            id='row-type-change'
            style={{ position: 'relative', width: '100%' }}>
            <TypeChip
              lesionAllInfo={lesionAllInfo}
              isExpanded={isExpanded}
              nested={nested}
              changeType={onTypeChange}
              smallScreen={smallScreen}
            />
          </div>
        </TableCell>

        {/* Class */}
        <TableCell
          style={{ width: '17%' }}
          className={classes.table_cell_general}
          align='center'
          data-no-drag='true'
          id='class_chip'
          onPointerDown={(e) => {
            if (e) {
              e.stopPropagation();
              e.preventDefault();
            }
          }}>
          {isExpanded ? (
            <div className={classes.empty_cell_container} />
          ) : (
            <div className={classes.general_cell_container}>
              <ClassCell
                nested={nested}
                lesionAllInfo={lesionAllInfo}
                handleClassChangeWrapper={onClassChange}
                smallScreen={smallScreen}
              />
            </div>
          )}
        </TableCell>

        {/* Lesion Size */}
        <TableCell className={classes.table_cell_general} align='center' style={{ width: '15%' }}>
          <div className={classes.general_cell_container}>
            <SizeCell
              lesionAllInfo={lesionAllInfo}
              projection={currentProjection}
              isExpanded={isExpanded}
              nested={nested}
            />
          </div>
        </TableCell>

        {/* Depth & Location & Quadrant */}
        <TableCell className={classes.table_cell_general} align='center' style={{ width: '11%' }}>
          <div className={classes.general_cell_container}>
            <Quadrant
              lesionAllInfo={lesionAllInfo}
              projection={currentProjection || lesionAllInfo?.breast}
              finding_class={finding_class}
              isExpanded={isExpanded}
              isClustered={isClustered}
              matched={matched}
              smallScreen={smallScreen}
            />
          </div>
        </TableCell>

        {/* Priors History Trend */}
        <TableCell className={classes.table_cell_general} align='center' style={{ width: '23%' }}>
          <div className={classes.general_cell_container} style={{ overflowX: 'auto' }}>
            <HistoryCell
              nested={nested}
              matchNestedTable={matchNestedTable}
              isExpanded={isExpanded}
              lesionPriors={lesionPriors ? [...lesionPriors].reverse() : []}
            />
          </div>
        </TableCell>

        {/* Actions */}
        <TableCell
          className={classes.table_cell_actions}
          align='center'
          id='row-actions-icon'
          style={{ width: '6%' }}>
          <div className={classes.general_cell_container}>
            {!isExpanded ? (
              <RowActions
                lesionAllInfo={lesionAllInfo}
                isGrouped={isClustered}
                matched={matched}
                isMatchedMainRow={isMatchedMainRow}
                nested={nested}
                isHovered={isHovered}
                onDelete={onDelete}
                pinnedRow={pinnedRow}
                setPinnedRow={setPinnedRow}
              />
            ) : (
              <div className={classes.empty_cell_container} />
            )}
          </div>
        </TableCell>
      </TableRow>

      {/* Nested Table for Clustered findings */}
      {isClustered &&
        isExpanded &&
        lesionAllInfo?.nested_items?.map((nestedFinding, idx) => (
          <DiagnosticsTableRow
            key={nestedFinding.finding_id || `nested-${idx}`}
            lesionAllInfo={formatNestedFinding(nestedFinding)}
            index={idx}
            nested={true}
            {...sharedRowProps}
          />
        ))}

      {/* Matched nested table */}
      {isExpanded &&
        matchedNestedItems?.matched_lesions?.map((matchedFinding, idx) => {
          const formatted = formatNestedFinding(matchedFinding);
          return (
            <DiagnosticsTableRow
              key={matchedFinding.finding_id}
              lesionAllInfo={formatted}
              index={`${formatted.projection[0]}-${formatted.projection[1]}-${formatted.projection[2]}`}
              nested={true}
              matchNestedTable={true}
              {...sharedRowProps}
            />
          );
        })}
    </>
  );
};

export default memo(DiagnosticsTableRow, (prevProps, nextProps) => {
  if (prevProps.lesionAllInfo !== nextProps.lesionAllInfo) return false;
  if (prevProps.index !== nextProps.index) return false;
  if (prevProps.nested !== nextProps.nested) return false;
  if (prevProps.matchNestedTable !== nextProps.matchNestedTable) return false;
  if (prevProps.draggingLesionId !== nextProps.draggingLesionId) return false;
  if (prevProps.justEditedRows !== nextProps.justEditedRows) return false;
  if (prevProps.exam_id !== nextProps.exam_id) return false;
  if (prevProps.smallScreen !== nextProps.smallScreen) return false;
  const prevIsMatchedMain = !!prevProps.lesionAllInfo?.isMatchedMainRow;
  const prevIsClustered = !!(prevProps.lesionAllInfo?.clustered && !prevIsMatchedMain);
  const nextIsMatchedMain = !!nextProps.lesionAllInfo?.isMatchedMainRow;
  const nextIsClustered = !!(nextProps.lesionAllInfo?.clustered && !nextIsMatchedMain);
  const prevPinId =
    getGroupedObjectId(prevProps.lesionAllInfo, prevIsClustered, prevIsMatchedMain) ||
    prevProps.lesionAllInfo?.finding_id;
  const nextPinId =
    getGroupedObjectId(nextProps.lesionAllInfo, nextIsClustered, nextIsMatchedMain) ||
    nextProps.lesionAllInfo?.finding_id;
  const wasPinned = prevProps.pinnedRow === prevPinId;
  const isPinned = nextProps.pinnedRow === nextPinId;
  if (wasPinned !== isPinned) return false;

  // Granular: only re-render if THIS row's expanded state changed
  if (
    computeRowIsExpanded(prevProps.lesionAllInfo, prevProps.expandedClusters) !==
    computeRowIsExpanded(nextProps.lesionAllInfo, nextProps.expandedClusters)
  )
    return false;

  return true;
});
